import React, { useState } from "react";
import { Modal, Spin, Form, Input, Button, Select, message } from "antd";
import { useQuery } from "react-query";
import {
	getEvaluationByUserId,
	updateEvaluation,
} from "../../Requests/evaluations-requests";
import { useTranslation } from "react-i18next";
import {
	getCurrentUniversityYear,
	useIsAdmin,
	useIsDean,
	useIsDepartmentDirector,
} from "../../utils/utilFunctions";
import { QualificationEnum } from "../../Api";
import {
	NOTIFICATION_TYPES,
	openNotification,
} from "../Notifications/NotificationsUtils";

const { Option } = Select;

interface ViewScoresModalProps {
	userId: string;
	visible: boolean;
	onClose: () => void;
}

const ViewScoresModal: React.FC<ViewScoresModalProps> = ({
	userId,
	visible,
	onClose,
}) => {
	const { t } = useTranslation();
	const [form] = Form.useForm();
	const [hasError, setHasError] = useState(false);
	const [selectedYear, setSelectedYear] = useState(
		getCurrentUniversityYear() - 1
	);
	const [availableYears, setAvailableYears] = useState<number[]>([]);
	const [selectedQualification, setSelectedQualification] = useState<
		string | null
	>(null);
	const isDean = useIsDean();
	const isDepartmentDirector = useIsDepartmentDirector();
	const isAdmin = useIsAdmin();

	const { data: evaluation, isLoading } = useQuery(
		["evaluationScores", userId, selectedYear],
		async () => {
			const response = await getEvaluationByUserId(userId, selectedYear);
			return response;
		},
		{
			enabled: visible && !!userId,
			onSuccess: (data) => {
				setHasError(false);
				form.setFieldsValue({
					selfEvaluationScore1: data.response?.selfEvaluationScore1,
					selfEvaluationScore2: data.response?.selfEvaluationScore2,
					peerEvaluationScore: data.response?.peerEvaluationScore,
					studentEvaluationScore: data.response?.studentEvaluationScore,
					departmentDirectorQualification:
						data.response?.departmentDirectorQualification,
				});

				setAvailableYears((prev) => [...new Set([...prev, selectedYear])]);
			},
			onError: () => {
				setHasError(true);
				form.resetFields();
			},
		}
	);

	const handleSave = async () => {
		try {
			const values = await form.validateFields();

			if (evaluation?.response) {
				await updateEvaluation(evaluation.response.id!, values);
			} else {
				throw new Error(t("evaluation.noData"));
			}
			openNotification(
				t("evaluation.scores"),
				t("evaluation.updateSuccess"),
				NOTIFICATION_TYPES.SUCCESS
			);
			onClose();
		} catch (error) {
			openNotification(
				t("evaluation.scores"),
				t("evaluation.updateError"),
				NOTIFICATION_TYPES.ERROR
			);
		}
	};

	const handleYearChange = (year: number) => {
		setSelectedYear(year);
	};

	const handleQualificationChange = (value: string) => {
		setSelectedQualification(value);
		console.log("Selected Qualification:", value);
	};

	const canEdit = isDean || isDepartmentDirector || isAdmin;

	return (
		<Modal
			title={t("evaluation.scores")}
			open={visible}
			onCancel={onClose}
			footer={[
				<Button key="cancel" onClick={onClose}>
					{t("common.cancel")}
				</Button>,
				<Button
					key="save"
					type="primary"
					onClick={handleSave}
					loading={isLoading}
					disabled={hasError}
				>
					{t("common.save")}
				</Button>,
			]}
		>
			<Select
				value={selectedYear}
				onChange={handleYearChange}
				style={{ marginBottom: 16, width: "100%" }}
			>
				{[...Array(5)].map((_, index) => {
					const year = getCurrentUniversityYear() - index - 1;
					return (
						<Option key={year} value={year}>
							{year} - {year + 1}
						</Option>
					);
				})}
			</Select>
			{isLoading ? (
				<Spin />
			) : hasError ? (
				<p>{t("evaluation.noData")}</p>
			) : (
				<>
					<Form form={form} layout="vertical">
						<Form.Item
							label={t("evaluation.selfEvaluation1")}
							name="selfEvaluationScore1"
							rules={[{ required: true, message: t("common.requiredField") }]}
						>
							<Input type="number" disabled={!canEdit} />
						</Form.Item>
						<Form.Item
							label={t("evaluation.selfEvaluation2")}
							name="selfEvaluationScore2"
							rules={[{ required: true, message: t("common.requiredField") }]}
						>
							<Input type="number" disabled={!canEdit} />
						</Form.Item>
						<Form.Item
							label={t("evaluation.peerEvaluation")}
							name="peerEvaluationScore"
							rules={[{ required: true, message: t("common.requiredField") }]}
						>
							<Input type="number" disabled={!canEdit} />
						</Form.Item>
						<Form.Item
							label={t("evaluation.studentEvaluation")}
							name="studentEvaluationScore"
							rules={[{ required: true, message: t("common.requiredField") }]}
						>
							<Input type="number" disabled={!canEdit} />
						</Form.Item>
						<Form.Item
							label={t("evaluation.departmentDirectorQualification")}
							name="departmentDirectorQualification"
							rules={[{ required: true, message: t("common.requiredField") }]}
						>
							<Select
								value={selectedQualification}
								onChange={handleQualificationChange}
								style={{ width: "100%" }}
								disabled={!canEdit}
							>
								{Object.values(QualificationEnum).map((value) => (
									<Option key={value} value={value}>
										{t(`qualification.${value}`)}
									</Option>
								))}
							</Select>
						</Form.Item>
					</Form>
				</>
			)}
		</Modal>
	);
};

export default ViewScoresModal;
